import { Lead, SiteMeta } from '../types';
import { ClientBrand, ALL_CLIENTS } from '../data/clients';

const LEADS_STORAGE_KEY = 'swaroop_leads_db_v1';
const AUTH_TOKEN_KEY = 'swaroop_admin_token_v1';
const CLIENTS_STORAGE_KEY = 'swaroop_clients_db_v1';

export const DEFAULT_SITE_META: SiteMeta = {
  siteName: 'Swaroop Web Solutions',
  tagline: 'Engineering Excellence for the Modern Web & AI Search',
  contactEmail: 'care@swaroopwebsolutions.com',
  notificationEmail: 'swaroopwebsolutions@gmail.com',
  supportPhone: '+91 91759 31452',
  whatsappNumber: '919175931452',
  locationCity: 'Pune',
  locationState: 'Maharashtra',
  locationCountry: 'India',
  geoLatitude: '18.5204',
  geoLongitude: '73.8567',
  primaryServices: [
    'Hosting & Website Annual Maintenance Contract (AMC)',
    'Website Design',
    'Website Development',
    'SEO (Search Engine Optimization) & AI-SEO',
    'Social Media Marketing',
    'E-Commerce & Commercial Portals'
  ]
};

async function readJsonResponse<T>(response: Response): Promise<T | null> {
  const raw = await response.text();
  if (!raw.trim()) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export async function fetchClients(): Promise<ClientBrand[]> {
  try {
    const response = await fetch('/api/clients.php', { cache: 'no-store' });
    const result = await readJsonResponse<{ success?: boolean; clients?: ClientBrand[] }>(response);
    if (response.ok && result?.success && Array.isArray(result.clients)) {
      const overrides = new Map(result.clients.map((client: ClientBrand) => [client.id, client]));
      return [...ALL_CLIENTS.map(client => overrides.get(client.id) || client), ...result.clients.filter((client: ClientBrand) => !ALL_CLIENTS.some(seed => seed.id === client.id))];
    }
  } catch {
    // Keep the built-in client list if the hosting API is blank or offline.
  }
  return ALL_CLIENTS;
}

export async function saveClient(client: ClientBrand): Promise<ClientBrand[]> {
  const token = getAdminToken();
  const response = await fetch('/api/clients.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
    body: JSON.stringify({ action: 'save', ...client })
  });
  const raw = await response.text();
  let result: { success?: boolean; clients?: ClientBrand[]; error?: string } = {};
  try { result = raw ? JSON.parse(raw) : {}; } catch { result = { error: 'The hosting server returned an invalid response.' }; }
  if (!response.ok || !result.success) throw new Error(result.error || 'Unable to save client');
  localStorage.setItem(CLIENTS_STORAGE_KEY, JSON.stringify(result.clients));
  return fetchClients();
}

export async function deleteClient(id: string): Promise<ClientBrand[]> {
  const token = getAdminToken();
  const response = await fetch('/api/clients.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
    body: JSON.stringify({ action: 'delete', id })
  });
  const result = await readJsonResponse<{ success?: boolean; error?: string }>(response);
  if (!response.ok || !result?.success) throw new Error(result?.error || 'Unable to delete client');
  return fetchClients();
}

export const DEFAULT_SITE_LOGO = '/swaroop-web-solutions-logo.png?v=20260822';

let siteLogoRequest: Promise<string> | null = null;
export async function fetchSiteLogo(): Promise<string> {
  if (siteLogoRequest) return siteLogoRequest;
  siteLogoRequest = (async () => {
  try {
    const response = await fetch(`/api/site-logo.php?v=${Date.now()}`, { cache: 'no-store' });
    const result = await readJsonResponse<{ success?: boolean; logoUrl?: string }>(response);
    return response.ok && result?.success ? result.logoUrl || '' : '';
  } catch { return ''; }
  })();
  return siteLogoRequest;
}

export async function saveSiteLogo(logo: File | string): Promise<string> {
  const headers: Record<string, string> = { Authorization: `Bearer ${getAdminToken() || ''}` };
  let body: BodyInit;
  if (logo instanceof File) {
    const form = new FormData();
    form.append('logo', logo, logo.name);
    body = form;
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({ logoUrl: logo });
  }
  const response = await fetch('/api/site-logo.php', { method: 'POST', headers, body });
  const raw = await response.text();
  let result: { success?: boolean; logoUrl?: string; error?: string } = {};
  try { result = raw ? JSON.parse(raw) : {}; } catch { result = { error: 'The hosting server returned an invalid response.' }; }
  if (!response.ok || !result.success) throw new Error(result.error || 'Unable to save website logo');
  siteLogoRequest = Promise.resolve(result.logoUrl || '');
  window.dispatchEvent(new CustomEvent('site-logo-updated', { detail: result.logoUrl || '' }));
  return result.logoUrl || '';
}

const INITIAL_LEADS: Lead[] = [
  {
    id: 'lead-101',
    fullName: 'Rajesh Sharma',
    email: 'rajesh.sharma@techgrowth.in',
    phone: '+91 98230 45678',
    website: 'https://techgrowth.in',
    service: 'Hosting & Website Annual Maintenance Contract (AMC)',
    budget: '$1,500 - $3,000',
    details: 'Need proactive server hosting management, automated daily backups, and security patching under annual maintenance contract.',
    createdAt: new Date(Date.now() - 3600000 * 4).toISOString(),
    status: 'new',
    source: 'website_contact',
    emailSent: true,
    whatsappNotified: true,
    notes: 'High priority lead. Discussed hosting AMC coverage.'
  },
  {
    id: 'lead-102',
    fullName: 'Priya Deshmukh',
    email: 'priya@deshmukhhospitality.com',
    phone: '+91 97654 32109',
    website: 'https://deshmukhhospitality.com',
    service: 'Website Development',
    budget: '$3,000 - $5,000',
    details: 'Looking for a responsive, high-speed website that runs seamlessly on standard shared cPanel hosting.',
    createdAt: new Date(Date.now() - 3600000 * 24).toISOString(),
    status: 'contacted',
    source: 'website_contact',
    emailSent: true,
    whatsappNotified: true,
    notes: 'Followed up via WhatsApp. Waiting for project requirements doc.'
  }
];

export function getLocalLeads(): Lead[] {
  try {
    const raw = localStorage.getItem(LEADS_STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(INITIAL_LEADS));
      return INITIAL_LEADS;
    }
    return JSON.parse(raw);
  } catch (e) {
    console.warn('Storage read error, using fallback', e);
    return INITIAL_LEADS;
  }
}

export function saveLead(leadData: Omit<Lead, 'id' | 'createdAt' | 'status' | 'source'>): Lead {
  const existing = getLocalLeads();
  const newLead: Lead = {
    ...leadData,
    id: 'lead-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 6),
    createdAt: new Date().toISOString(),
    status: 'new',
    source: 'website_contact',
    emailSent: true,
    whatsappNotified: false
  };

  const updated = [newLead, ...existing];
  try {
    localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to save lead to local storage', e);
  }
  return newLead;
}

/**
 * Submits lead to dynamic server endpoint /api/contact.php
 */
export async function submitContactInquiry(leadData: {
  fullName: string;
  email: string;
  phone: string;
  website?: string;
  service: string;
  budget?: string;
  details: string;
  honeypot?: string;
}): Promise<{ success: boolean; lead?: Lead; error?: string; message?: string }> {
  try {
    const response = await fetch('/api/contact.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(leadData)
    });

    const result = await response.json().catch(() => null);

    if (response.ok && result?.success && result?.lead) {
      // Keep in local cache for admin offline view
      const existing = getLocalLeads();
      const updated = [result.lead, ...existing.filter(item => item.id !== result.lead.id)];
      localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(updated));
      return { success: true, lead: result.lead, message: result.message };
    } else {
      return { success: false, error: result?.error || `Server responded with HTTP ${response.status}` };
    }
  } catch (e) {
    console.error('Contact submission network error:', e);
    return {
      success: false,
      error: 'Unable to connect to the inquiry submission server. Please try again.'
    };
  }
}

/**
 * Dynamic Central Leads retrieval from /api/leads.php
 */
export async function fetchCentralLeads(): Promise<Lead[]> {
  const token = getAdminToken();
  if (token) {
    try {
      const response = await fetch('/api/leads.php', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && Array.isArray(result.leads)) {
          localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(result.leads));
          return result.leads;
        }
      }
    } catch (e) {
      console.warn('Failed to fetch from central leads API', e);
    }
  }
  return getLocalLeads();
}

/**
 * Updates lead status across server and local store
 */
export async function updateLeadStatus(id: string, status: Lead['status'], notes?: string): Promise<Lead[]> {
  const token = getAdminToken();
  if (token) {
    try {
      const response = await fetch('/api/leads.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'update_status', id, status, notes })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && Array.isArray(result.leads)) {
          localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(result.leads));
          return result.leads;
        }
      }
    } catch (e) {
      console.warn('Server update error, updating local state', e);
    }
  }

  const existing = getLocalLeads();
  const updated = existing.map(item => {
    if (item.id === id) {
      return {
        ...item,
        status,
        ...(notes !== undefined ? { notes } : {})
      };
    }
    return item;
  });
  localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

/**
 * Deletes lead across server and local store
 */
export async function deleteLead(id: string): Promise<Lead[]> {
  const token = getAdminToken();
  if (token) {
    try {
      const response = await fetch('/api/leads.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'delete', id })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && Array.isArray(result.leads)) {
          localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(result.leads));
          return result.leads;
        }
      }
    } catch (e) {
      console.warn('Server delete error, updating local state', e);
    }
  }

  const existing = getLocalLeads();
  const updated = existing.filter(item => item.id !== id);
  localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

export function exportLeadsToCSV(leads: Lead[]): void {
  const headers = ['ID', 'Date', 'Full Name', 'Email', 'Phone', 'Service', 'Budget', 'Website', 'Status', 'Details', 'Notes'];
  const rows = leads.map(l => [
    l.id,
    new Date(l.createdAt).toLocaleString(),
    `"${l.fullName.replace(/"/g, '""')}"`,
    `"${l.email.replace(/"/g, '""')}"`,
    `"${(l.phone || '').replace(/"/g, '""')}"`,
    `"${l.service.replace(/"/g, '""')}"`,
    `"${(l.budget || '').replace(/"/g, '""')}"`,
    `"${(l.website || '').replace(/"/g, '""')}"`,
    l.status,
    `"${l.details.replace(/"/g, '""')}"`,
    `"${(l.notes || '').replace(/"/g, '""')}"`
  ]);

  const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `swaroop-leads-${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function generateWhatsAppLeadAlertUrl(lead: Lead): string {
  const message = `🔔 *NEW LEAD ALERT - SWAROOP WEB SOLUTIONS*\n\n` +
    `👤 *Name:* ${lead.fullName}\n` +
    `📧 *Email:* ${lead.email}\n` +
    `📱 *Phone:* ${lead.phone || 'Not provided'}\n` +
    `🌐 *Website:* ${lead.website || 'N/A'}\n` +
    `🛠️ *Service:* ${lead.service}\n` +
    `💰 *Budget:* ${lead.budget || 'Flexible'}\n\n` +
    `📝 *Details:* ${lead.details}\n\n` +
    `⏰ *Timestamp:* ${new Date(lead.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}`;
  
  return `https://api.whatsapp.com/send?phone=919175931452&text=${encodeURIComponent(message)}`;
}

export function generateWhatsAppClientReplyUrl(lead: Lead): string {
  const phone = (lead.phone || '').replace(/[^0-9]/g, '');
  const cleanPhone = phone.startsWith('91') ? phone : phone.length === 10 ? `91${phone}` : phone;
  const message = `Hi ${lead.fullName}, thank you for reaching out to *Swaroop Web Solutions* regarding *${lead.service}*. We reviewed your project details and would love to assist you. When is a good time for a quick 10-minute discovery call?`;
  
  return `https://api.whatsapp.com/send?phone=${cleanPhone || '919175931452'}&text=${encodeURIComponent(message)}`;
}

// Session Authentication & Token Storage
export function getAdminToken(): string | null {
  try {
    return sessionStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function isAdminAuthenticated(): boolean {
  try {
    const token = sessionStorage.getItem(AUTH_TOKEN_KEY);
    return Boolean(token && token.length > 20);
  } catch {
    return false;
  }
}

export function setAdminAuthenticated(auth: boolean, token?: string): void {
  if (auth && token) {
    sessionStorage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

/**
 * Authenticates admin credentials strictly with backend API
 */
export async function authenticateAdminServer(
  email: string,
  password: string,
  captchaExpected: number,
  captchaAnswer: number
): Promise<{ success: boolean; error?: string; token?: string }> {
  try {
    const res = await fetch('/api/admin-auth.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, captchaExpected, captchaAnswer })
    });

    const data = await res.json().catch(() => null);
    if (res.ok && data?.success && data?.token) {
      setAdminAuthenticated(true, data.token);
      return { success: true, token: data.token };
    } else {
      return { 
        success: false, 
        error: data?.error || (res.status === 429 ? 'Too many login attempts. Please wait 15 minutes.' : 'Authentication failed') 
      };
    }
  } catch (e) {
    console.error('Backend auth endpoint unreachable:', e);
    return {
      success: false,
      error: 'Unable to connect to authorization server. Please verify PHP API configuration.'
    };
  }
}
